import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import './Dashboard.css';
import SummaryDashboard from './SummaryDashboard';
import DetailedAnalysis from './DetailedAnalysis';
import DataExplorer from './DataExplorer';
import DayBreakdown from './DayBreakdown';
import Logo from './Logo';
import { fetchSponsorLog, fetchCalendar } from '../utils/api';
import { resolveType } from '../utils/dataProcessor';
import availableTournaments from '../assets/availableTournaments.json';

const TABS = {
  SUMMARY: 'summary',
  DETAILED: 'detailed',
  EXPLORER: 'explorer',
  DAY_BREAKDOWN: 'daybreakdown',
};

const Dashboard = ({ data }) => {
  const [currentTab, setCurrentTab] = useState(TABS.SUMMARY);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [tournamentId, setTournamentId] = useState('all'); // default to 'all' for year-wide data
  const [selectedTournamentType, setSelectedTournamentType] = useState('all'); // filter tournaments by type
  const [remoteData, setRemoteData] = useState(null); // normalized sponsor logs
  const [_apiExtra, setApiExtra] = useState({ hourly: [], daily: [], summary: [], raw: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const [filters, setFilters] = useState({
    tournaments: [],
    dates: [],
    courts: [],
    graphics: [],
    tournamentTypes: []
  });
  const [tournaments, setTournaments] = useState([]); // calendar tournaments for selected year
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [_calendarEndpoint, setCalendarEndpoint] = useState(null); // debug: endpoint used for calendar fetch
  const calendarAbortRef = useRef(null);

  // Filter tournaments by selected type
  const filteredTournaments = useMemo(() => {
    const allAvailableIds = new Set();
    Object.values(availableTournaments).forEach(ids => {
      ids.forEach(id => allAvailableIds.add(String(id)));
    });
    
    if (selectedTournamentType === 'all') {
      return tournaments.filter(t => {
        const id = String(t.atpId || t.wCompCompetitionInstanceID || t.id);
        return allAvailableIds.has(id);
      });
    }
    return tournaments.filter(t => {
      const id = t.atpId || t.wCompCompetitionInstanceID || t.id;
      const type = resolveType(id, { Tournament: t.tournamentName || t.instanceName, EventId: id });
      return type === selectedTournamentType && allAvailableIds.has(String(id));
    });
  }, [tournaments, selectedTournamentType]);

  // Get unique tournament types from current tournaments
  const availableTournamentTypes = useMemo(() => {
    const types = new Set();
    tournaments.forEach(t => {
      const id = t.atpId || t.wCompCompetitionInstanceID || t.id;
      const type = resolveType(id, { Tournament: t.tournamentName || t.instanceName, EventId: id });
      if (type) types.add(type);
    });
    const typeArray = Array.from(types);
    const typeOrder = ['1000', '500', '250', 'WTF', 'NextGen', 'Other'];
    return typeArray.sort((a, b) => {
      const indexA = typeOrder.indexOf(a);
      const indexB = typeOrder.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
  }, [tournaments]);

  // Use remote data if available else fallback to passed-in data
  const effectiveData = remoteData !== null ? remoteData : data;

  // Filter data by tournament type if selected
  const tournamentTypeFilteredData = useMemo(() => {
    if (!effectiveData || effectiveData.length === 0) return [];
    if (selectedTournamentType === 'all') return effectiveData;
    
    // Filter by tournament type — check curated JSON first, then fall back to heuristic
    return effectiveData.filter(record => {
      const type = resolveType(record.EventId, { Tournament: record.Tournament, EventId: record.EventId });
      return type === selectedTournamentType;
    });
  }, [effectiveData, selectedTournamentType]);

  // Filter data for Summary (current selected year only)
  const summaryData = useMemo(() => {
    if (!tournamentTypeFilteredData || tournamentTypeFilteredData.length === 0) return [];
    return tournamentTypeFilteredData.filter(record => String(record.Year) === String(year));
  }, [tournamentTypeFilteredData, year]);

  // All data for GFX Analysis and Raw Data (includes all years, but filtered by tournament type)
  const allData = useMemo(() => {
    return tournamentTypeFilteredData || [];
  }, [tournamentTypeFilteredData]);
  
  const performFetch = (force = false) => {
    const hasLocalData = Array.isArray(data) && data.length > 0;
    const shouldFetchRemote = tournamentId !== 'all';

    if (!force && (!shouldFetchRemote || (tournamentId === 'all' && hasLocalData))) {
      setRemoteData(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);

    const fetchPromise = tournamentId === 'all'
      ? Promise.resolve({ sponsorLogs: data, hourly: [], daily: [], summary: [], raw: null })
      : fetchSponsorLog(year, tournamentId, { signal: controller.signal });

    fetchPromise
      .then(result => {
        setRemoteData(result.sponsorLogs);
        setApiExtra({ hourly: result.hourly, daily: result.daily, summary: result.summary, raw: result.raw });
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        console.error('Dashboard fetch error:', err);
        setError(err.message || 'Unknown error');
        setRemoteData([]);
        setApiExtra({ hourly: [], daily: [], summary: [], raw: null });
      })
      .finally(() => setLoading(false));
  };

  // Fetch on mount & when parameters change
 useEffect(() => {
  performFetch();
  return () => {
    if (abortRef.current) abortRef.current.abort();
  };
}, [year, tournamentId, data]);

  // Fetch calendar tournaments when year changes (only once per year)
  useEffect(() => {
    // Skip if we already have tournaments for this year
    if (tournaments.length > 0) return;
    
    // Abort previous calendar request
    if (calendarAbortRef.current) {
      calendarAbortRef.current.abort();
    }
    const controller = new AbortController();
    calendarAbortRef.current = controller;
    setCalendarLoading(true);
    setCalendarError(null);
    fetchCalendar(year, { signal: controller.signal })
      .then(result => {
        // New return shape: { tournaments, endpoint }
        const { tournaments: list, endpoint } = result || {};
        setTournaments(Array.isArray(list) ? list : []);
        setCalendarEndpoint(endpoint || null);
        // If current tournamentId not in list and not 'all', default to 'all'
        if (Array.isArray(list) && list.length > 0 && tournamentId !== 'all' && !list.some(t => t.id === tournamentId)) {
          setTournamentId('all');
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        console.error('Calendar fetch error:', err);
        setCalendarError(err.message || 'Calendar fetch failed');
        setTournaments([]);
        setCalendarEndpoint(null);
      })
      .finally(() => setCalendarLoading(false));
  }, [year]);

  const handleRefresh = () => {
    performFetch(true);
  };

  // Reset tournament selection if it's not in the filtered list
  useEffect(() => {
    if (selectedTournamentType !== 'all' && tournamentId !== 'all') {
      const tournamentInFilteredList = filteredTournaments.some(t => t.id === tournamentId);
      if (!tournamentInFilteredList) {
        setTournamentId('all');
      }
    }
  }, [selectedTournamentType, filteredTournaments, tournamentId]);


  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  }, []);

  const renderTabContent = () => {
    switch (currentTab) {
      case TABS.SUMMARY:
        // Summary shows 2025 data for metrics, but needs all data for InsightsLineChart comparison
            return <SummaryDashboard data={summaryData} allData={allData} year={year} tournaments={tournaments} />;
      case TABS.DETAILED:
        // GFX Analysis shows all data (including 2024) for full analysis
        return (
          <DetailedAnalysis 
            data={allData} 
            filters={filters} 
            onFilterChange={handleFilterChange}
          />
        );
      case TABS.EXPLORER:
        // Raw Data uses lifted fetch data
        return (
          <DataExplorer 
            data={allData}
            filters={filters} 
            onFilterChange={handleFilterChange}
          />
        );
      case TABS.DAY_BREAKDOWN:
        return (
          <DayBreakdown
            data={allData}
            tournamentId={tournamentId}
            tournament={filteredTournaments.find(t => t.id === tournamentId) || null}
          />
        );
        default:
            return <SummaryDashboard data={summaryData} allData={allData} tournaments={tournaments} />;
    }
  };

  return (
    <div className="dashboard-container">
      <div className="main-content">
        <div className="header">
          <div className="logo-container">
            <div className="atp-logo-placeholder">
              <Logo />
              <p>Sponsor Dashboard</p>
            </div>
          </div>
          <div className="parameter-controls">
            <label>
              Year:
              <select value={year} onChange={(e) => setYear(e.target.value)} className="param-select">
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
              </select>
            </label>
            <label>
              Tournament Type:
              <select 
                value={selectedTournamentType} 
                onChange={(e) => setSelectedTournamentType(e.target.value)} 
                className="param-select"
                disabled={calendarLoading || tournaments.length === 0}
              >
                <option value="all">All Types</option>
                {availableTournamentTypes.map(type => (
                  <option key={type} value={type}>
                    {type === '1000' ? 'ATP 1000 (Masters)' : 
                     type === '500' ? 'ATP 500' : 
                     type === '250' ? 'ATP 250' : 
                     type === 'WTF' ? 'ATP Finals' :
                     type === 'NextGen' ? 'NextGen Finals' :
                     type}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Tournament:
              <select
                value={tournamentId}
                onChange={(e) => setTournamentId(e.target.value)}
                className="param-select"
                disabled={calendarLoading || filteredTournaments.length === 0}
                style={{ minWidth: '240px' }}
              >
                {calendarLoading && <option>Loading tournaments...</option>}
                {!calendarLoading && filteredTournaments.length === 0 && <option>No tournaments</option>}
                {!calendarLoading && filteredTournaments.length > 0 && (
                  <>
                    <option value="all">{selectedTournamentType === 'all' ? 'All Tournaments' : `All ${selectedTournamentType === '1000' ? 'ATP 1000' : selectedTournamentType === '500' ? 'ATP 500' : selectedTournamentType === '250' ? 'ATP 250' : selectedTournamentType} Tournaments`}</option>
                    {filteredTournaments.map(t => {
                      const display = t.tournamentName && t.tournamentName !== t.instanceName
                        ? `${t.instanceName} – ${t.tournamentName}`
                        : t.tournamentName || t.instanceName || t.id;
                      const extra = [t.city, t.surface].filter(Boolean).join(' · ');
                      const start = t.startDate ? new Date(t.startDate) : null;
                      const end = t.endDate ? new Date(t.endDate) : null;
                      const fmt = (d) => d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '?';
                      const range = start && end ? `${fmt(start)} – ${fmt(end)}` : 'Dates N/A';
                      return (
                        <option key={t.id} value={t.id} title={`${display} | ${range}`}>                        
                          {display}{extra ? ` (${extra})` : ''}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
            </label>
            <button 
              className="param-refresh-button" 
              onClick={handleRefresh} 
              disabled={loading}
              style={{marginTop: '24px'}}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {(error || calendarError) && (
              <span className="param-error" style={{ color: 'var(--error-color, #c00)', marginLeft: '12px', maxWidth: '320px' }}>
                {error || calendarError}
              </span>
            )}
          </div>
        </div>

        <div className="tab-navigation">
          <button 
            className={`tab-button ${currentTab === TABS.SUMMARY ? 'active' : ''}`}
            onClick={() => setCurrentTab(TABS.SUMMARY)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 13H7L9 7L15 17L17 11H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M3 3V21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Summary
          </button>
          <button 
            className={`tab-button ${currentTab === TABS.DETAILED ? 'active' : ''}`}
            onClick={() => setCurrentTab(TABS.DETAILED)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2"/>
            </svg>
            GFX Analysis
          </button>
          <button 
            className={`tab-button ${currentTab === TABS.EXPLORER ? 'active' : ''}`}
            onClick={() => setCurrentTab(TABS.EXPLORER)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.5 5.25C3.5 4.83579 3.16421 4.5 2.75 4.5C2.33579 4.5 2 4.83579 2 5.25V17.25C2 18.4926 3.00736 19.5 4.25 19.5H21.25C21.6642 19.5 22 19.1642 22 18.75C22 18.3358 21.6642 18 21.25 18H4.25C3.83579 18 3.5 17.6642 3.5 17.25V5.25Z" fill="currentColor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M7 10.2773C5.89543 10.2773 5 11.1728 5 12.2773V15.7501C5 16.1643 5.33579 16.5001 5.75 16.5001H8.25C8.66421 16.5001 9 16.1643 9 15.7501V12.2773C9 11.1728 8.10457 10.2773 7 10.2773ZM6.5 12.2773C6.5 12.0012 6.72386 11.7773 7 11.7773C7.27614 11.7773 7.5 12.0012 7.5 12.2773V15.0001H6.5V12.2773Z" fill="currentColor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 6.5C10.5 5.39543 11.3954 4.5 12.5 4.5C13.6046 4.5 14.5 5.39543 14.5 6.5V15.7501C14.5 16.1643 14.1642 16.5001 13.75 16.5001H11.25C10.8358 16.5001 10.5 16.1643 10.5 15.7501V6.5ZM12.5 6C12.2239 6 12 6.22386 12 6.5V15.0001H13V6.5C13 6.22386 12.7761 6 12.5 6Z" fill="currentColor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M18 8.05859C16.8954 8.05859 16 8.95402 16 10.0586V15.7501C16 16.1643 16.3358 16.5001 16.75 16.5001H19.25C19.6642 16.5001 20 16.1643 20 15.7501V10.0586C20 8.95402 19.1046 8.05859 18 8.05859ZM17.5 10.0586C17.5 9.78245 17.7239 9.55859 18 9.55859C18.2761 9.55859 18.5 9.78245 18.5 10.0586V15.0001H17.5V10.0586Z" fill="currentColor"/>
            </svg>
            Raw Data
          </button>
          <button
            className={`tab-button ${currentTab === TABS.DAY_BREAKDOWN ? 'active' : ''}`}
            onClick={() => setCurrentTab(TABS.DAY_BREAKDOWN)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M3 9H21" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M7 13H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M7 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Day Breakdown
          </button>
        </div>

        <div className="tab-content">
          {loading && (
            <div className="loading-overlay">
              <p>Loading data...</p>
            </div>
          )}
          {!loading && error && (
            <div className="error-overlay">
              <p>Error: {error}</p>
              <button onClick={handleRefresh}>Retry</button>
            </div>
          )}
          {!loading && !error && renderTabContent()}
          {/* Future: components could use apiExtra.hourly/daily/summary for charts */}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;